import React from 'react';
import styled from '@emotion/styled';
import { transparentize } from 'polished';
import { withSpacingStyles } from '../withSpacingStyles';
import { Text } from '../text/Text';
import { useComponentTheme } from '../useComponentTheme';

interface Columns {
  key: string;
  title: string;
  dataIndex: string;
  dataTest?: string;
  width?: String;
}

type Props = {
  columns: Columns,
  dataSource: any,
  className?: string,
  mode?: string,
  hideHeader?: Boolean,
  title?: String,
  dashed?: Boolean,
  fixedLayout?: Boolean,
  dataTest?: String,
  titleDataTest?: String,
  width?: String,
  wordWrap?: Boolean,
  cellWidth?: String,
};

const _Table = ({
  columns,
  dataSource,
  className,
  hideHeader,
  title,
  dashed,
  fixedLayout,
  titleDataTest,
  dataTest,
  mode,
  width,
  wordWrap,
  cellWidth,
}: Props) => {
  const componentTheme = useComponentTheme(theme => theme.components.Table, mode);

  if (!dataSource) {
    return null;
  }

  return (
    <Wrapper componentTheme={componentTheme} width={width} className={className}>
      {title && (
        <Text level={1} mode={mode} mb={4} display="block" dataTest={titleDataTest}>
          {title}
        </Text>
      )}

     <TableWrapper componentTheme={componentTheme} fixedLayout={fixedLayout}>
        {!hideHeader && (
          <thead>
            <TableRow componentTheme={componentTheme}>
              {columns.map(({ key, title, width }) => (
                <TableHeadCell key={key} componentTheme={componentTheme} width={width}>
                  {title}
                </TableHeadCell>
              ))}
            </TableRow>
          </thead>
        )}

        <tbody>
          {dataSource.map(row => (
            <TableBodyRow key={row.key} componentTheme={componentTheme} dashed={dashed} data-test={`${dataTest}-row`}>
              {columns.map(column => (
                <TableCell
                  componentTheme={componentTheme}
                  data-test={column.dataTest ? column.dataTest(row.dataTest) : row.dataTest}
                  width={column.width}
                  key={`${row.key}-${column.key}`}
                  wordWrap={wordWrap}
                  cellWidth={cellWidth}
                >
                  {row[column.dataIndex]}
                </TableCell>
              ))}
            </TableBodyRow>
          ))}
        </tbody>
      </TableWrapper>
    </Wrapper>
  );
};

export const Table = withSpacingStyles(_Table);

const Wrapper = styled('div')(
  ({ componentTheme, width }) => `
  border: ${componentTheme.border};
  border-radius: ${componentTheme.borderRadius};
  padding: ${componentTheme.padding};
  background: ${componentTheme.backgroundColor};
  ${width ? `width: ${width};` : ''}
  overflow: auto;
`
);

const TableWrapper = styled('table')(
  ({ componentTheme, fixedLayout }) => `
  text-align: ${componentTheme.textAlign};
  width: 100%;
  border-collapse: collapse;
  ${fixedLayout ? 'table-layout: fixed;' : ''}
  font-size: ${componentTheme.fontSize};
`
);

const TableRow = styled('tr')(
  ({ componentTheme }) => `
  border-radius: ${componentTheme.Row.borderRadius};
  border-bottom: ${componentTheme.Row.border};
`
);

const TableBodyRow = styled(TableRow)(
  ({ theme, dashed }) =>
    dashed &&
    `:nth-of-type(odd) {
        background: ${transparentize(0.85, theme.color.lightGrey)}
      }
    `,
    `
      :last-child {
        border-bottom: 0;
      }
    `
);

const TableCell = styled('td')(
  ({ theme, componentTheme, wordWrap, cellWidth, width = 0 }) => `
  padding: ${componentTheme.Cell.padding};
  font-weight: ${componentTheme.Cell.fontWeight};
  border: ${componentTheme.Cell.border};
  font-size:  ${componentTheme.Cell.fontSize};
  ${wordWrap ? `overflow-wrap: break-word;` : ''};

  ${width ? `
    width: ${width};
    white-space: nowrap;
  ` : ''}

  @media (${theme.breakpoint.tablet}) {
    ${cellWidth && `width: ${cellWidth}`};
  }
`
);

const TableHeadCell = styled(TableCell)(
  ({ componentTheme }) => `
  padding: ${componentTheme.HeadCell.padding};
  font-weight: ${componentTheme.HeadCell.fontWeight};
  border: ${componentTheme.HeadCell.border};
  font-size:  ${componentTheme.HeadCell.fontSize};
`
);
